/**
 * FikoKisitSolver.ts — Kalıcı 2D geometrik kısıt modeli + iteratif çözücü (motor çekirdeği)
 *
 * Angular'dan bağımsız, salt veri + matematik. Mevcut tek-seferlik kısıtların
 * (FikoModellemeYonetici.kisitParalel/kisitDik...) ÜSTÜNE oturan kalıcı katman:
 * kısıtlar bir kez tanımlanır, sürükleme/ölçü değişiminde solver.coz() çağrılarak
 * KORUNUR (CATIA/SolidWorks parametrik davranış).
 *
 * Yaklaşım: her ISketchEleman'ın serbest skaler değerleri (nokta x/y, çember r)
 * tek bir değişken vektörüne (degiskenler) toplanır. Her kısıt bir "residual"
 * (hata) üretir; iteratif düzeltme döngüsü (kısıt-projeksiyon + sönümlü
 * Gauss-Newton adımı) bu hataları minimize eder. Küçük sketch'ler (onlarca
 * eleman) için yeterince hızlı ve kararlı; donma yok (sabit iterasyon tavanı).
 *
 * Saf-class: signal/Angular yok. Sketch'e referansla çalışır; geometriyi
 * yerinde günceller, ardından çağıran sketch.ciz() yapar.
 */

import { FikoSketch, ISketchEleman, ISketchNokta } from './FikoSketch';

export type KisitTip =
  | 'coincidence'   // iki uç nokta çakışık
  | 'horizontal'    // çizgi yatay
  | 'vertical'      // çizgi dikey
  | 'parallel'      // iki çizgi paralel
  | 'perpendicular' // iki çizgi dik
  | 'tangent'       // çizgi-çember teğet
  | 'equal'         // iki çizgi eşit boy / iki çember eşit yarıçap
  | 'concentric'    // iki çember eşmerkez
  | 'collinear'     // iki çizgi aynı doğru üstünde
  | 'fix'           // eleman/nokta sabit (kilit)
  | 'distance'      // iki nokta arası mesafe = parametre
  | 'radius'        // çember yarıçapı = parametre
  | 'angle'         // iki çizgi arası açı = parametre (derece)
  | 'symmetry'      // iki nokta bir eksen-çizgisine göre simetrik [A, B, eksenÇizgi]
  | 'middlePoint'   // bir nokta iki ucun tam ortası [orta, A, B]
  | 'equidistant';  // |AB| = |AC| (üç nokta, eşit mesafe) [A, B, C]

/** Bir kısıtın hedeflediği eleman + (varsa) ucu. uc: 0/1 nokta indeksi; yoksa eleman geneli. */
export interface IKisitHedef { elemanId: number; uc?: number; }

export interface IKisit {
  id: number;
  tip: KisitTip;
  elemanlar: IKisitHedef[];
  parametre?: number;   // distance/radius/angle için sayısal değer
}

/** Solver değişken haritası: hangi skaler nerede yaşıyor (eleman + alan). */
interface IDegiskenRef {
  elemanId: number;
  alan: 'px' | 'py' | 'r';   // nokta x, nokta y, yarıçap
  uc: number;                // px/py için nokta indeksi (r için 0)
}

const EPS = 1e-7;
const DERECE = Math.PI / 180;

export class FikoKisitSolver {
  kisitlar: IKisit[] = [];
  private sayac = 1;

  // çözüm sırasında sabitlenen değişkenler (sürüklenen nokta, fix kısıtları)
  private kilitli = new Set<string>();
  private degiskenler: IDegiskenRef[] = [];
  private indeksHarita = new Map<string, number>();   // "elemanId:alan:uc" → vektör indeksi

  constructor(private sketch: FikoSketch) {}

  // ---- kısıt CRUD ----

  ekle(tip: KisitTip, elemanlar: IKisitHedef[], parametre?: number): IKisit {
    const k: IKisit = { id: this.sayac++, tip, elemanlar, parametre };
    this.kisitlar.push(k);
    return k;
  }

  sil(id: number): void { this.kisitlar = this.kisitlar.filter(k => k.id !== id); }

  /** Bir eleman silindiğinde ona bağlı tüm kısıtları temizle (cascade). */
  elemanKisitlariSil(elemanId: number): void {
    this.kisitlar = this.kisitlar.filter(k => !k.elemanlar.some(e => e.elemanId === elemanId));
  }

  temizle(): void { this.kisitlar = []; }

  /** Belirli bir elemana bağlı kısıtlar (glyph çizimi/inceleme için). */
  elemanKisitlari(elemanId: number): IKisit[] {
    return this.kisitlar.filter(k => k.elemanlar.some(e => e.elemanId === elemanId));
  }

  // ---- DOF ----

  /**
   * Serbestlik derecesi (DOF). degisken sayısı - bağımsız kısıt denklemi sayısı.
   * Yaklaşık (overlap'leri tam ayrıştırmaz) ama CATIA "under/fully/over defined"
   * rozeti için yeterli ipucu verir.
   */
  dof(): { degisken: number; denklem: number; serbestlik: number; durum: 'serbest' | 'tam' | 'asiri' } {
    this.degiskenHaritaKur();
    const degisken = this.degiskenler.length;
    let denklem = 0;
    for (const k of this.kisitlar) denklem += this.kisitDenklemSayisi(k);
    const serbestlik = degisken - denklem;
    const durum = serbestlik > 0 ? 'serbest' : serbestlik === 0 ? 'tam' : 'asiri';
    return { degisken, denklem, serbestlik, durum };
  }

  private kisitDenklemSayisi(k: IKisit): number {
    switch (k.tip) {
      case 'coincidence': return 2;
      case 'horizontal': case 'vertical': case 'parallel': case 'perpendicular':
      case 'tangent': case 'equal': case 'collinear': case 'distance': case 'radius':
      case 'angle': return k.tip === 'collinear' ? 2 : 1;
      case 'concentric': return 2;
      case 'fix': return 2;   // x ve y kilidi
      case 'symmetry': return 2;       // orta-nokta eksende + AB eksene dik
      case 'middlePoint': return 2;    // x ve y
      case 'equidistant': return 1;    // tek skaler eşitlik
      default: return 0;
    }
  }

  // ---- değişken haritası ----

  private anahtar(elemanId: number, alan: string, uc: number): string { return `${elemanId}:${alan}:${uc}`; }

  /** Sketch elemanlarından serbest skaler değişken listesi kur. */
  private degiskenHaritaKur(): void {
    this.degiskenler = [];
    this.indeksHarita.clear();
    const ekle = (elemanId: number, alan: 'px' | 'py' | 'r', uc: number) => {
      const a = this.anahtar(elemanId, alan, uc);
      if (this.indeksHarita.has(a)) return;
      this.indeksHarita.set(a, this.degiskenler.length);
      this.degiskenler.push({ elemanId, alan, uc });
    };
    for (const el of this.sketch.elemanlar) {
      if (el.tip === 'cizgi' || el.tip === 'dikdortgen') {
        ekle(el.id, 'px', 0); ekle(el.id, 'py', 0); ekle(el.id, 'px', 1); ekle(el.id, 'py', 1);
      } else if (el.tip === 'cember') {
        ekle(el.id, 'px', 0); ekle(el.id, 'py', 0); ekle(el.id, 'r', 0);
      } else if (el.tip === 'yay') {
        for (let i = 0; i < 3; i++) { ekle(el.id, 'px', i); ekle(el.id, 'py', i); }
      } else if (el.tip === 'nokta') {
        ekle(el.id, 'px', 0); ekle(el.id, 'py', 0);
      }
    }
  }

  private idx(elemanId: number, alan: string, uc: number): number {
    const i = this.indeksHarita.get(this.anahtar(elemanId, alan, uc));
    return i === undefined ? -1 : i;
  }

  private vektorOku(): number[] {
    const v = new Array(this.degiskenler.length);
    for (let i = 0; i < this.degiskenler.length; i++) {
      const d = this.degiskenler[i];
      const el = this.sketch.elemanlar.find(e => e.id === d.elemanId);
      if (!el) { v[i] = 0; continue; }
      v[i] = d.alan === 'r' ? (el.r || 0) : (d.alan === 'px' ? el.noktalar[d.uc].x : el.noktalar[d.uc].y);
    }
    return v;
  }

  private vektorYaz(v: number[]): void {
    for (let i = 0; i < this.degiskenler.length; i++) {
      const d = this.degiskenler[i];
      const el = this.sketch.elemanlar.find(e => e.id === d.elemanId);
      if (!el) continue;
      if (d.alan === 'r') el.r = Math.max(0.01, v[i]);
      else if (d.alan === 'px') el.noktalar[d.uc].x = v[i];
      else el.noktalar[d.uc].y = v[i];
    }
  }

  // ---- residual fonksiyonları ----

  private elemanGetir(id: number): ISketchEleman | undefined { return this.sketch.elemanlar.find(e => e.id === id); }

  /** Bir kısıtın residual (hata) değerleri — sıfıra yaklaşması istenir. v: güncel değişken vektörü. */
  private residual(k: IKisit, v: number[]): number[] {
    const gx = (id: number, uc: number) => { const i = this.idx(id, 'px', uc); return i >= 0 ? v[i] : (this.elemanGetir(id)?.noktalar[uc]?.x ?? 0); };
    const gy = (id: number, uc: number) => { const i = this.idx(id, 'py', uc); return i >= 0 ? v[i] : (this.elemanGetir(id)?.noktalar[uc]?.y ?? 0); };
    const gr = (id: number) => { const i = this.idx(id, 'r', 0); return i >= 0 ? v[i] : (this.elemanGetir(id)?.r ?? 0); };
    const e = k.elemanlar;

    switch (k.tip) {
      case 'coincidence': {
        const a = e[0], b = e[1];
        return [gx(a.elemanId, a.uc ?? 0) - gx(b.elemanId, b.uc ?? 0), gy(a.elemanId, a.uc ?? 0) - gy(b.elemanId, b.uc ?? 0)];
      }
      case 'horizontal': { const a = e[0]; return [gy(a.elemanId, 1) - gy(a.elemanId, 0)]; }
      case 'vertical': { const a = e[0]; return [gx(a.elemanId, 1) - gx(a.elemanId, 0)]; }
      case 'parallel': {
        const [l1, l2] = e;
        const d1x = gx(l1.elemanId, 1) - gx(l1.elemanId, 0), d1y = gy(l1.elemanId, 1) - gy(l1.elemanId, 0);
        const d2x = gx(l2.elemanId, 1) - gx(l2.elemanId, 0), d2y = gy(l2.elemanId, 1) - gy(l2.elemanId, 0);
        return [(d1x * d2y - d1y * d2x) / (this.norm(d1x, d1y) * this.norm(d2x, d2y) + EPS)]; // cross=0
      }
      case 'perpendicular': {
        const [l1, l2] = e;
        const d1x = gx(l1.elemanId, 1) - gx(l1.elemanId, 0), d1y = gy(l1.elemanId, 1) - gy(l1.elemanId, 0);
        const d2x = gx(l2.elemanId, 1) - gx(l2.elemanId, 0), d2y = gy(l2.elemanId, 1) - gy(l2.elemanId, 0);
        return [(d1x * d2x + d1y * d2y) / (this.norm(d1x, d1y) * this.norm(d2x, d2y) + EPS)]; // dot=0
      }
      case 'collinear': {
        const [l1, l2] = e;
        const ax = gx(l1.elemanId, 0), ay = gy(l1.elemanId, 0);
        const bx = gx(l1.elemanId, 1), by = gy(l1.elemanId, 1);
        const dx = bx - ax, dy = by - ay; const L = this.norm(dx, dy) + EPS;
        const nx = -dy / L, ny = dx / L;
        const c0x = gx(l2.elemanId, 0), c0y = gy(l2.elemanId, 0);
        const c1x = gx(l2.elemanId, 1), c1y = gy(l2.elemanId, 1);
        return [(c0x - ax) * nx + (c0y - ay) * ny, (c1x - ax) * nx + (c1y - ay) * ny]; // her iki uç doğruya 0 mesafe
      }
      case 'equal': {
        const [a, b] = e;
        const ea = this.elemanGetir(a.elemanId), eb = this.elemanGetir(b.elemanId);
        if (ea?.tip === 'cizgi' && eb?.tip === 'cizgi') {
          const la = this.norm(gx(a.elemanId, 1) - gx(a.elemanId, 0), gy(a.elemanId, 1) - gy(a.elemanId, 0));
          const lb = this.norm(gx(b.elemanId, 1) - gx(b.elemanId, 0), gy(b.elemanId, 1) - gy(b.elemanId, 0));
          return [la - lb];
        }
        return [gr(a.elemanId) - gr(b.elemanId)]; // çember/çember
      }
      case 'concentric': {
        const [a, b] = e;
        return [gx(a.elemanId, 0) - gx(b.elemanId, 0), gy(a.elemanId, 0) - gy(b.elemanId, 0)];
      }
      case 'tangent': {
        const [a, b] = e;
        const ea = this.elemanGetir(a.elemanId), eb = this.elemanGetir(b.elemanId);
        const ciz = ea?.tip === 'cizgi' ? a : b;
        const cem = ea?.tip === 'cember' ? a : b;
        const ax = gx(ciz.elemanId, 0), ay = gy(ciz.elemanId, 0);
        const bx = gx(ciz.elemanId, 1), by = gy(ciz.elemanId, 1);
        const dx = bx - ax, dy = by - ay; const L = this.norm(dx, dy) + EPS;
        const nx = -dy / L, ny = dx / L;
        const mx = gx(cem.elemanId, 0), my = gy(cem.elemanId, 0);
        const mesafe = (mx - ax) * nx + (my - ay) * ny;
        return [Math.abs(mesafe) - gr(cem.elemanId)]; // |dik mesafe| = r
      }
      case 'fix': {
        const a = e[0]; const el = this.elemanGetir(a.elemanId); if (!el) return [0, 0];
        const uc = a.uc ?? 0;
        // referans = elemanın çözüm öncesi (snapshot) konumu; sabit kalsın
        return [gx(a.elemanId, uc) - (this._fixRef.get(this.anahtar(a.elemanId, 'px', uc)) ?? gx(a.elemanId, uc)),
                gy(a.elemanId, uc) - (this._fixRef.get(this.anahtar(a.elemanId, 'py', uc)) ?? gy(a.elemanId, uc))];
      }
      case 'distance': {
        const [a, b] = e; const hedef = k.parametre ?? 0;
        return [this.norm(gx(a.elemanId, a.uc ?? 0) - gx(b.elemanId, b.uc ?? 0), gy(a.elemanId, a.uc ?? 0) - gy(b.elemanId, b.uc ?? 0)) - hedef];
      }
      case 'radius': { const a = e[0]; return [gr(a.elemanId) - (k.parametre ?? gr(a.elemanId))]; }
      case 'angle': {
        const [l1, l2] = e; const hedef = (k.parametre ?? 0) * DERECE;
        const a1 = Math.atan2(gy(l1.elemanId, 1) - gy(l1.elemanId, 0), gx(l1.elemanId, 1) - gx(l1.elemanId, 0));
        const a2 = Math.atan2(gy(l2.elemanId, 1) - gy(l2.elemanId, 0), gx(l2.elemanId, 1) - gx(l2.elemanId, 0));
        let d = a2 - a1 - hedef; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI;
        return [d];
      }
      case 'symmetry': {
        const [pa, pb, ax] = e;
        const aX = gx(pa.elemanId, pa.uc ?? 0), aY = gy(pa.elemanId, pa.uc ?? 0);
        const bX = gx(pb.elemanId, pb.uc ?? 0), bY = gy(pb.elemanId, pb.uc ?? 0);
        const e0x = gx(ax.elemanId, 0), e0y = gy(ax.elemanId, 0);
        const e1x = gx(ax.elemanId, 1), e1y = gy(ax.elemanId, 1);
        const dx = e1x - e0x, dy = e1y - e0y; const L = this.norm(dx, dy) + EPS;
        const ux = dx / L, uy = dy / L;          // eksen yön birimi
        const nx = -uy, ny = ux;                  // eksen normali
        const mx = (aX + bX) / 2, my = (aY + bY) / 2;
        return [
          (mx - e0x) * nx + (my - e0y) * ny,      // orta nokta eksen üstünde (dik mesafe=0)
          (bX - aX) * ux + (bY - aY) * uy,        // AB eksene dik (eksen yönü bileşeni=0)
        ];
      }
      case 'middlePoint': {
        const [m, a, b] = e;
        const mX = gx(m.elemanId, m.uc ?? 0), mY = gy(m.elemanId, m.uc ?? 0);
        const aX = gx(a.elemanId, a.uc ?? 0), aY = gy(a.elemanId, a.uc ?? 0);
        const bX = gx(b.elemanId, b.uc ?? 0), bY = gy(b.elemanId, b.uc ?? 0);
        return [mX - (aX + bX) / 2, mY - (aY + bY) / 2];
      }
      case 'equidistant': {
        const [a, b, c] = e;
        const aX = gx(a.elemanId, a.uc ?? 0), aY = gy(a.elemanId, a.uc ?? 0);
        const bX = gx(b.elemanId, b.uc ?? 0), bY = gy(b.elemanId, b.uc ?? 0);
        const cX = gx(c.elemanId, c.uc ?? 0), cY = gy(c.elemanId, c.uc ?? 0);
        return [this.norm(bX - aX, bY - aY) - this.norm(cX - aX, cY - aY)];
      }
      default: return [];
    }
  }

  private _fixRef = new Map<string, number>();

  private norm(x: number, y: number): number { return Math.sqrt(x * x + y * y); }

  // ---- iteratif çözüm ----

  /**
   * Kısıtları çöz. `kilitlerNoktalari`: çözüm sırasında SABİT tutulacak değişkenler
   * (örn. sürüklenen nokta → "elemanId:px:uc" / "elemanId:py:uc"). Bu sayede
   * kullanıcının elindeki nokta yerinde kalır, diğer geometri ona göre uyum sağlar.
   */
  coz(kilitlerNoktalari: string[] = [], maksIter = 60): boolean {
    if (!this.kisitlar.length) return true;
    this.degiskenHaritaKur();
    if (!this.degiskenler.length) return true;

    // fix referanslarını çöz öncesi snapshot'la
    this._fixRef.clear();
    for (const k of this.kisitlar) {
      if (k.tip !== 'fix') continue;
      const a = k.elemanlar[0]; const el = this.elemanGetir(a.elemanId); if (!el) continue;
      const uc = a.uc ?? 0;
      if (el.noktalar[uc]) { this._fixRef.set(this.anahtar(a.elemanId, 'px', uc), el.noktalar[uc].x); this._fixRef.set(this.anahtar(a.elemanId, 'py', uc), el.noktalar[uc].y); }
    }

    this.kilitli = new Set(kilitlerNoktalari);
    let v = this.vektorOku();
    const n = v.length;

    const SONUM = 1e-4;   // Levenberg sönümleme (kararlılık)
    const ADIM = 0.9;     // alt-relaksasyon

    let sonHata = Infinity;
    for (let iter = 0; iter < maksIter; iter++) {
      // residual + sayısal Jacobian
      const { res, jac } = this.jacobianHesapla(v);
      if (!res.length) break;
      const hata = res.reduce((s, r) => s + r * r, 0);
      if (hata < 1e-10) { sonHata = hata; break; }
      if (Math.abs(sonHata - hata) < 1e-12 && iter > 4) { sonHata = hata; break; } // yakınsadı/takıldı
      sonHata = hata;

      // Gauss-Newton normal denklem: (JᵀJ + λI) Δ = -Jᵀr
      const m = res.length;
      const JtJ: number[][] = Array.from({ length: n }, () => new Array(n).fill(0));
      const Jtr: number[] = new Array(n).fill(0);
      for (let i = 0; i < m; i++) {
        for (let a = 0; a < n; a++) {
          if (jac[i][a] === 0) continue;
          Jtr[a] += jac[i][a] * res[i];
          for (let b = 0; b < n; b++) JtJ[a][b] += jac[i][a] * jac[i][b];
        }
      }
      for (let a = 0; a < n; a++) JtJ[a][a] += SONUM;

      // kilitli değişkenleri denklemden düşür (satır/sütun → birim, sağ taraf 0)
      for (let a = 0; a < n; a++) {
        const d = this.degiskenler[a];
        const key = `${d.elemanId}:${d.alan}:${d.uc}`;
        if (!this.kilitli.has(key)) continue;
        for (let b = 0; b < n; b++) { JtJ[a][b] = 0; JtJ[b][a] = 0; }
        JtJ[a][a] = 1; Jtr[a] = 0;
      }

      const delta = this.lineerCoz(JtJ, Jtr);
      if (!delta) break;
      for (let a = 0; a < n; a++) v[a] -= ADIM * delta[a];
    }

    this.vektorYaz(v);
    return sonHata < 1e-4;
  }

  /** Residual vektörü + sayısal Jacobian (merkezi fark). */
  private jacobianHesapla(v: number[]): { res: number[]; jac: number[][] } {
    const tumRes: number[] = [];
    const parcalar: { k: IKisit; baslangic: number; boy: number }[] = [];
    for (const k of this.kisitlar) {
      const r = this.residual(k, v);
      parcalar.push({ k, baslangic: tumRes.length, boy: r.length });
      for (const x of r) tumRes.push(x);
    }
    const m = tumRes.length, n = v.length;
    const jac: number[][] = Array.from({ length: m }, () => new Array(n).fill(0));
    const h = 1e-5;
    for (let a = 0; a < n; a++) {
      const eski = v[a];
      v[a] = eski + h; const arti = this.tumResidual(v, parcalar);
      v[a] = eski - h; const eksi = this.tumResidual(v, parcalar);
      v[a] = eski;
      for (let i = 0; i < m; i++) jac[i][a] = (arti[i] - eksi[i]) / (2 * h);
    }
    return { res: tumRes, jac };
  }

  private tumResidual(v: number[], parcalar: { k: IKisit; baslangic: number; boy: number }[]): number[] {
    const out: number[] = [];
    for (const p of parcalar) { const r = this.residual(p.k, v); for (const x of r) out.push(x); }
    return out;
  }

  /** Küçük yoğun lineer sistem çöz (Gauss eliminasyonu + kısmi pivot). */
  private lineerCoz(A: number[][], b: number[]): number[] | null {
    const n = b.length;
    const M = A.map((row, i) => [...row, b[i]]);
    for (let col = 0; col < n; col++) {
      let pivot = col;
      for (let r = col + 1; r < n; r++) if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r;
      if (Math.abs(M[pivot][col]) < 1e-12) continue; // tekil sütun → atla (DOF artığı)
      [M[col], M[pivot]] = [M[pivot], M[col]];
      const pv = M[col][col];
      for (let r = 0; r < n; r++) {
        if (r === col) continue;
        const f = M[r][col] / pv;
        if (f === 0) continue;
        for (let c = col; c <= n; c++) M[r][c] -= f * M[col][c];
      }
    }
    const x = new Array(n).fill(0);
    for (let i = 0; i < n; i++) { const pv = M[i][i]; x[i] = Math.abs(pv) < 1e-12 ? 0 : M[i][n] / pv; }
    return x;
  }

  // ---- kalıcılık ----

  serialize(): any { return { v: 1, kisitlar: this.kisitlar, sayac: this.sayac }; }

  deserialize(o: any): void {
    if (!o) return;
    this.kisitlar = o.kisitlar || [];
    this.sayac = o.sayac || (this.kisitlar.length + 1);
  }

  // ---- glyph yerleşimi (FikoSketch.kisitlariCiz için 2D düzlem konumları) ----

  /**
   * Her kalıcı kısıt için glyph (simge) çizilecek 2D düzlem noktası + tip.
   * FikoSketch render katmanı bunları okur; burada sadece konum/tip üretilir.
   */
  glyphler(): { tip: KisitTip; p: ISketchNokta; aci?: number }[] {
    const out: { tip: KisitTip; p: ISketchNokta; aci?: number }[] = [];
    const orta = (id: number): ISketchNokta | null => {
      const el = this.elemanGetir(id); if (!el) return null;
      if (el.tip === 'cizgi') { const [a, b] = el.noktalar; return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; }
      if (el.tip === 'cember') return { x: el.noktalar[0].x, y: el.noktalar[0].y + (el.r || 0) };
      return { x: el.noktalar[0].x, y: el.noktalar[0].y };
    };
    const yon = (id: number): number => {
      const el = this.elemanGetir(id); if (!el || el.tip !== 'cizgi') return 0;
      const [a, b] = el.noktalar; return Math.atan2(b.y - a.y, b.x - a.x);
    };
    let kademe = 0;
    for (const k of this.kisitlar) {
      const t = k.elemanlar[0];
      let p = orta(t.elemanId); if (!p) continue;
      // birden çok glyph üst üste binmesin diye küçük kademe ofseti
      kademe = (kademe + 1) % 6;
      p = { x: p.x, y: p.y };
      out.push({ tip: k.tip, p, aci: yon(t.elemanId) });
    }
    return out;
  }
}
